import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminAPI } from '../services/api';

const TAB_VALUES = ['discover', 'feed', 'looking', 'chat', 'activity'];

const TAB_META = {
  discover: { label: 'Discover', icon: '🧭', color: '#6366f1', gradient: 'from-indigo-500 to-purple-500' },
  feed:     { label: 'Feed',     icon: '📰', color: '#3b82f6', gradient: 'from-blue-500 to-cyan-500' },
  looking:  { label: 'Looking',  icon: '👀', color: '#10b981', gradient: 'from-emerald-500 to-teal-500' },
  chat:     { label: 'Chat',     icon: '💬', color: '#f59e0b', gradient: 'from-amber-500 to-orange-500' },
  activity: { label: 'Activity', icon: '🔔', color: '#ef4444', gradient: 'from-red-500 to-pink-500' },
};

const DAY_OPTIONS = [7, 14, 30, 60, 90];

const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
};

const fmtDate = (str) => {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const fmtDateFull = (str) => {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

export default function Impressions() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminAPI.getImpressions({ days: d });
      const payload = res?.data?.tabImpressions ?? res?.tabImpressions ?? null;
      if (!payload) throw new Error('Unexpected response shape from API.');
      setData(payload);
    } catch (err) {
      setError(err?.message || 'Failed to load impression data.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const summary = useMemo(() => data?.summary ?? [], [data]);
  const totalImpressions = data?.totalImpressions ?? 0;
  const totalUniqueUsers = data?.totalUniqueUsers ?? 0;

  const dailyData = useMemo(() => {
    const raw = data?.daily ?? [];
    const dailyUniqueMap = Object.fromEntries(
      (data?.dailyUniqueUsers ?? []).map((d) => [d.date, d.uniqueUsers || 0])
    );
    const dateMap = {};
    raw.forEach(({ date, tab, impressions, uniqueUsers }) => {
      if (!dateMap[date]) dateMap[date] = { date, total: 0, totalUsers: dailyUniqueMap[date] ?? 0 };
      dateMap[date][tab] = { impressions, uniqueUsers };
      dateMap[date].total += impressions;
    });
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const todayData = useMemo(() => {
    if (!dailyData.length) return null;
    return dailyData[dailyData.length - 1];
  }, [dailyData]);

  const yesterdayData = useMemo(() => {
    if (dailyData.length < 2) return null;
    return dailyData[dailyData.length - 2];
  }, [dailyData]);

  const chartMax = useMemo(() => {
    return Math.max(...dailyData.map(d => d.total), 1);
  }, [dailyData]);

  const getChange = (today, yesterday) => {
    if (!yesterday || yesterday === 0) return null;
    return ((today - yesterday) / yesterday * 100).toFixed(1);
  };

  if (loading && !data) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-64" />
        <div className="grid grid-cols-5 gap-4">
          {[1,2,3,4,5].map(i => <div key={i} className="h-32 bg-gray-200 rounded-2xl" />)}
        </div>
        <div className="h-64 bg-gray-200 rounded-2xl" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Impressions</h1>
          <p className="text-gray-500 mt-1 text-sm">
            How often each app tab is visited. Updated in real-time.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  days === d
                    ? 'bg-pink-500 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => load(days)}
            disabled={loading}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? '...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Today's Overview - Big Numbers */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Today's Overview</h2>
              {todayData && (
                <span className="text-xs text-gray-400">{fmtDateFull(todayData.date)}</span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* Total today */}
              <div className="bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl p-4 text-white col-span-1">
                <div className="text-xs font-medium opacity-80">Total Today</div>
                <div className="text-2xl font-bold mt-1">{fmt(todayData?.total || 0)}</div>
                {yesterdayData && (() => {
                  const change = getChange(todayData?.total || 0, yesterdayData?.total || 0);
                  if (!change) return null;
                  return (
                    <div className={`text-xs mt-1 ${Number(change) >= 0 ? 'text-green-200' : 'text-red-200'}`}>
                      {Number(change) >= 0 ? '↑' : '↓'} {Math.abs(change)}% vs yesterday
                    </div>
                  );
                })()}
              </div>

              {/* Per tab today */}
              {TAB_VALUES.map(tab => {
                const today = todayData?.[tab]?.impressions || 0;
                const yesterday = yesterdayData?.[tab]?.impressions || 0;
                const change = getChange(today, yesterday);
                const meta = TAB_META[tab];
                return (
                  <div key={tab} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{meta.icon}</span>
                      <span className="text-xs font-semibold text-gray-500">{meta.label}</span>
                    </div>
                    <div className="text-xl font-bold text-gray-900">{fmt(today)}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400">{fmt(todayData?.[tab]?.uniqueUsers || 0)} users</span>
                      {change && (
                        <span className={`text-xs font-medium ${Number(change) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {Number(change) >= 0 ? '↑' : '↓'}{Math.abs(change)}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Period Summary */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">
                {days}-Day Summary
              </h2>
              <span className="text-sm text-gray-400">
                {data?.period && `${fmtDate(data.period.startDate)} → ${fmtDate(data.period.endDate)}`}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {TAB_VALUES.map(tab => {
                const row = summary.find(s => s.tab === tab) || { impressions: 0, uniqueUsers: 0 };
                const meta = TAB_META[tab];
                const share = totalImpressions > 0 ? ((row.impressions / totalImpressions) * 100).toFixed(1) : 0;
                return (
                  <div key={tab} className="relative overflow-hidden rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">{meta.icon}</span>
                      <span className="text-sm font-bold text-gray-700">{meta.label}</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{fmt(row.impressions)}</div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-400">{fmt(row.uniqueUsers)} users</span>
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: `${meta.color}15`, color: meta.color }}
                      >
                        {share}%
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${share}%`, background: meta.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Total impressions</span>
                <span className="text-lg font-bold text-gray-900">{fmt(totalImpressions)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Total unique users</span>
                <span className="text-lg font-bold text-gray-900">{fmt(totalUniqueUsers)}</span>
              </div>
            </div>
          </div>

          {/* Daily Chart */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Daily Trend</h2>

            {/* Stacked bar chart */}
            <div className="overflow-x-auto">
              <div className="flex items-end gap-1 h-48 min-w-[600px]">
                {dailyData.map((day, idx) => {
                  const isToday = idx === dailyData.length - 1;
                  return (
                    <div
                      key={day.date}
                      className="flex flex-col items-center flex-1 h-full justify-end group"
                    >
                      {/* Tooltip on hover */}
                      <div className="hidden group-hover:block absolute -top-8 bg-gray-900 text-white text-xs px-2 py-1 rounded z-10">
                        {fmt(day.total)}
                      </div>
                      {/* Stacked bars */}
                      <div
                        className={`w-full rounded-t-md transition-all duration-300 relative ${isToday ? 'ring-2 ring-pink-400 ring-offset-1' : ''}`}
                        style={{
                          height: `${Math.max((day.total / chartMax) * 100, 2)}%`,
                          background: isToday
                            ? 'linear-gradient(135deg, #ec4899, #8b5cf6)'
                            : 'linear-gradient(135deg, #6366f1, #3b82f6)',
                          opacity: isToday ? 1 : 0.6 + (idx / dailyData.length) * 0.4,
                        }}
                        title={`${fmtDateFull(day.date)}: ${day.total} impressions, ${day.totalUsers} users`}
                      />
                      {/* Date label - show every few */}
                      {(idx % Math.max(1, Math.floor(dailyData.length / 10)) === 0 || isToday) && (
                        <span className={`text-[10px] mt-1 ${isToday ? 'font-bold text-pink-600' : 'text-gray-400'}`}>
                          {fmtDate(day.date)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
              <span className="flex items-center gap-2 text-xs text-gray-500">
                <span className="w-3 h-3 rounded bg-gradient-to-r from-pink-500 to-purple-500" />
                Today
              </span>
              <span className="flex items-center gap-2 text-xs text-gray-500">
                <span className="w-3 h-3 rounded bg-gradient-to-r from-indigo-500 to-blue-500 opacity-70" />
                Past days
              </span>
            </div>
          </div>

          {/* Daily Breakdown Table */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Daily Breakdown</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                    {TAB_VALUES.map(tab => (
                      <th key={tab} className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                        {TAB_META[tab].icon} {TAB_META[tab].label}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Users</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[...dailyData].reverse().slice(0, 30).map((day, idx) => {
                    const isToday = idx === 0;
                    return (
                      <tr key={day.date} className={isToday ? 'bg-pink-50/50' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-3 text-sm">
                          <span className={isToday ? 'font-bold text-pink-600' : 'text-gray-700'}>
                            {fmtDateFull(day.date)}
                          </span>
                          {isToday && (
                            <span className="ml-2 text-[10px] bg-pink-100 text-pink-600 px-1.5 py-0.5 rounded-full font-bold">
                              TODAY
                            </span>
                          )}
                        </td>
                        {TAB_VALUES.map(tab => (
                          <td key={tab} className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                            {fmt(day[tab]?.impressions || 0)}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-center text-sm font-bold text-gray-900">
                          {fmt(day.total)}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-500">
                          {fmt(day.totalUsers)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Platform breakdown */}
          {(data?.byPlatform ?? []).length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">By Platform</h2>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    const grouped = {};
                    (data.byPlatform || []).forEach(({ platform, impressions }) => {
                      grouped[platform] = (grouped[platform] || 0) + impressions;
                    });
                    const total = Object.values(grouped).reduce((s, v) => s + v, 0);
                    return Object.entries(grouped)
                      .sort((a, b) => b[1] - a[1])
                      .map(([platform, count]) => (
                        <div key={platform} className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-700 w-20 capitalize">{platform}</span>
                          <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                              style={{ width: `${(count / total) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold text-gray-700 w-16 text-right">{fmt(count)}</span>
                          <span className="text-xs text-gray-400 w-12 text-right">
                            {((count / total) * 100).toFixed(1)}%
                          </span>
                        </div>
                      ));
                  })()}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
